"use client";

import {
  addToWishlist,
  createWishlist,
  deleteWishlistLineItem,
} from "@/api/saleor/services/wishlist";
import { algoliaEventParam, eventParam } from "@/apiUtils/algoliaUtils/constants";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import {
  getCartCount,
  getPromotionBadgeName,
  getWishlistIdByLocale,
  hasArrayElements,
  productLinkConstructor,
} from "@/apiUtils/commonUtils";
import { analyticBuyFlows, analyticEventTypes, cartErrors } from "@/apiUtils/constants";
import { buildProductPriceInfo } from "@/apiUtils/productUtils";
import {
  getFromCookieMetadataSA,
  setInCookieMetadataSA,
} from "@/apiUtils/cookieDataUtilsServerActions";
import PlusIcon from "@/assets/icons/add.svg?react";
import CheckIcon from "@/assets/icons/check.svg?react";
import HeartIcon from "@/assets/icons/heartIcon.svg";
import SpecsIcon from "@/assets/icons/specsIcon.svg";
import NoImageSrc from "@/assets/images/no_image.svg";
import {
  AvailablePromotion,
  MediaItem,
  Price,
  TranslationData,
  VariantItem,
  WishlistCreateResponse,
} from "@/common/lib/types";
import { useCustomToast } from "@/hooks/useCustomToast";
import { cn } from "@/lib/tailwind";
import { useGlobalStore } from "@/store/store";
import { addToCartById } from "@/utils/cart.utils";
import {
  getChannelIdInClient,
  getFieldValue,
  getLangFromLocale,
  isNonTransactionalCountry,
} from "@/utils/common";
import RenderCurrencyInText from "../common/RenderCurrencyInText";
import Image from "next/image";
import { useParams } from "next/navigation";
import { memo, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { CLink } from "../common/customLink/CustomLink";
import VisibilitySensor from "../common/VisibilitySensor";
import VtoModal from "../pdp/VtoModal";
import ProductPrice from "../productPrice/ProductPrice";
import Spinner from "../ui/spinner";
import AddToWishList from "./AddToWishlist";
import ProductImage from "./ProductImage";
import ProductImageCarousel from "./ProductImageCarousel";
import ProductVariantsCarousel from "./ProductVariantsCarousel";
import { Hit } from "instantsearch.js";
import { ProductItem, RecommendProductItem } from "@/apiUtils/algoliaUtils/types";
import {
  algoliaClickedObjectIDSClient,
  algoliaClickEventClient,
  algoliaWishlistConversionClient,
} from "@/apiUtils/algoliaUtils/indexClient";

export type ImageStyle = {
  height: number;
  width: number;
  classname?: string;
};

export type Pricing = {
  costPrice: { net: number; gross: number } | Price;
  price: { net: number; gross: number } | Price;
  priceUndiscounted: { net: number; gross: number } | Price;
  discount: { net: number; gross: number } | Price;
  discountPercent?: number | null;
};

export interface IProductCardProps {
  name?: string;
  description?: string;
  pricing?: Pricing;
  productTileBadge?: ReactNode;
  tryOn?: boolean;
  ean?: string;
  sku?: string;
  productImages: MediaItem[];
  productImageStyles: ImageStyle;
  colorOptions?: string[];
  addToCart?: boolean;
  likeBtn?: boolean;
  productVariants?: VariantItem[];
  showProductCarousel?: boolean;
  productLink?: string;
  promotionalDetails?: AvailablePromotion[];
  handleSelectVariant: (item: VariantItem) => void;
  selectedVariant: VariantItem;
  isChevronVariant?: boolean;
  isDefaultVariant?: boolean;
  onComplete: () => void;
  translations?: TranslationData;
  pageType?: string;
  queryID?: string;
  objectID?: string;
  indexName?: string;
  index?: number;
  onVisible?: (isVisible: any) => void;
  source?: string;
  sourceId?: string;
  isNudgeAnimationPresent?: boolean;
  slug?: string;
  altText: string;
  productItem?: Hit<ProductItem> | RecommendProductItem;
  showCartAddedState?: boolean;
  onDeferToast?: (imgUrl: string) => void;
}

const ProductCard = ({
  colorOptions,
  description,
  name,
  ean,
  sku,
  addToCart = true,
  likeBtn = true,
  productTileBadge,
  tryOn,
  productImages,
  productVariants,
  productImageStyles,
  showProductCarousel = true,
  productLink,
  promotionalDetails,
  handleSelectVariant,
  selectedVariant,
  isChevronVariant = true,
  isDefaultVariant,
  onComplete,
  translations,
  pageType,
  pricing,
  index,
  queryID,
  objectID,
  indexName,
  onVisible = () => {},
  source,
  sourceId,
  slug,
  isNudgeAnimationPresent,
  altText,
  productItem,
  showCartAddedState = false,
  onDeferToast,
}: IProductCardProps) => {
  //const
  const variantId = selectedVariant?.variantId;

  //hooks
  const { locale } = useParams();
  const language = getLangFromLocale(locale as string);
  const isNonTransactional = useMemo(() => isNonTransactionalCountry(locale as string), [locale]);
  // const { ref, isIntersecting } = useIntersectionObserver({ rootMargin: "0px", threshold: 0 });

  const {
    cartCount,
    cartVariantIds,
    wishlistedProducts,
    user,
    addProductToWishlist,
    removeProductFromWishlist,
    updateCartCount,
    addCartVariantId,
    isAuthenticated,
  } = useGlobalStore((state) => state);

  const isAlreadyInCart = showCartAddedState && cartVariantIds.includes(variantId);

  // const selectedProductImage = selectedVariant?.media[0]?.url.startsWith("/")
  //   ? // `https://mcdn.magrabi.com/image/upload/c_limit,w_384/f_auto/q_95/v1/p/prod/i${selectedVariant?.media[0]?.url}`
  //     (getCloudinaryURL(selectedVariant?.media[0]?.url) ?? NoImageSrc.src)
  //   : selectedVariant?.media[0]?.url;

  const selectedProductImage = selectedVariant?.media[0]?.url;

  //state

  const [isWishlisted, setIsWishlisted] = useState(wishlistedProducts?.includes(variantId));
  const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false);
  const { showToast } = useCustomToast();
  const [vtoOpen, setVtoOpen] = useState(false);
  const [resetProductImageCarousel, setResetProductImageCarousel] = useState(false);
  const [openVariantCarousel, setOpenVariantCarousel] = useState(false);

  const plpPriceInfo = useMemo(() => {
    const [promotionDetails] = promotionalDetails || [];
    const undiscountedPrice =
      (typeof pricing?.costPrice?.gross === "number"
        ? pricing?.costPrice?.gross
        : pricing?.costPrice?.gross?.amount) ?? 0;
    const salePrice =
      (typeof pricing?.price?.gross === "number"
        ? pricing?.price?.gross
        : pricing?.price?.gross?.amount) ??
      (typeof pricing?.priceUndiscounted?.gross === "number"
        ? pricing?.priceUndiscounted?.gross
        : pricing?.priceUndiscounted?.gross?.amount) ??
      0;

    return buildProductPriceInfo(undiscountedPrice, salePrice, promotionDetails, pricing);
  }, [pricing, promotionalDetails]);

  //hooks
  const cardRef = useRef<HTMLDivElement | null>(null);
  const prevScrollPosImageCarousel = useRef(0);
  const prevScrollPosVariantCarousel = useRef(0);

  // console.log(index === 2 ? { isIntersecting, selectedVariant } : "");
  // useEffect(() => {
  //   console.log("getting val", window.innerHeight);
  //   console.log(index === 2 ? "entering here" : "");
  //   if (isIntersecting === false) {
  //     if (Array.isArray(plpScrollArr[0])) {
  //       for (const fn of plpScrollArr[0]) {
  //         fn((prev) => (prev === true ? false : prev));
  //       }
  //     }
  //   }
  // }, [isIntersecting]);

  //functions
  function handleSetOpenVariantCarousel(val: boolean) {
    setOpenVariantCarousel(val);
  }

  function handleSetResetProductImageCarousel(val: boolean) {
    setResetProductImageCarousel(val);
  }

  const handleRemoveFromWishlist = async () => {
    setIsWishlisted(false);
    removeProductFromWishlist(variantId);

    try {
      const wishlistId: string = await getFromCookieMetadataSA(
        getWishlistIdByLocale(locale as string),
      );
      const response = await deleteWishlistLineItem(wishlistId, variantId);
      if (response?.wishlist) {
        showToast({
          text: translations?.common?.common_product_removed_from_wishlist_message ?? "",
          lang: language,
          icon: HeartIcon,
          // redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
        });
      } else {
        showToast({
          text: translations?.common?.common_add_to_cart_error_toast_message ?? "",
          lang: language,
          icon: HeartIcon,
        });
        setIsWishlisted(true);
        addProductToWishlist(variantId);
      }
    } catch (error) {
      setIsWishlisted(true);
      addProductToWishlist(variantId);
      showToast({
        text: translations?.common?.common_add_to_cart_error_toast_message ?? "",
        lang: language,
        icon: HeartIcon,
      });
    }
  };

  const handleAddToWishlist = async () => {
    setIsWishlisted(true);
    addProductToWishlist(variantId);
    try {
      const wishlistId: string = await getFromCookieMetadataSA(
        getWishlistIdByLocale(locale as string),
      );
      if (wishlistId) {
        const response = await addToWishlist(wishlistId, variantId, !!isDefaultVariant);
        if (response?.wishlist?.id) {
          showToast({
            text: translations?.common?.common_product_added_to_wishlist_message!,
            lang: language,
            icon: HeartIcon,
            redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
          });

          setInCookieMetadataSA(getWishlistIdByLocale(locale as string), response.wishlist.id);
          handlePostAnalytics({
            eventType: analyticEventTypes.ACTIONS,
            data: {
              target: analyticEvents.ADD_TO_WISHLIST,
              product: { ...productItem, name: name, hitIndex: index || 0, selectedQuantity: 1 },
              selectedVariantId: selectedVariant?.variantId,
              source,
              sourceId,
            },
          });
          if (indexName) {
            algoliaWishlistConversionClient(
              indexName,
              eventParam.WISHLIST_ADD,
              variantId,
              queryID,
              user?.magrabi_id,
            );
          }
        } else {
          showToast({
            text: translations?.common?.common_add_to_cart_error_toast_message ?? "",
            lang: language,
            icon: HeartIcon,
          });
          setIsWishlisted(false);
          removeProductFromWishlist(variantId);
        }
      } else {
        const channel = getChannelIdInClient(locale as string);
        const response: WishlistCreateResponse = await createWishlist(channel, [
          { variantId, isDefaultVariant: !!isDefaultVariant },
        ]);
        if (response?.wishlist?.id) {
          showToast({
            text: translations?.common?.common_product_added_to_wishlist_message ?? "",
            lang: language,
            icon: HeartIcon,
            redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
          });
          setInCookieMetadataSA(getWishlistIdByLocale(locale as string), response.wishlist.id);
          await handlePostAnalytics({
            eventType: analyticEventTypes.ACTIONS,
            data: {
              target: analyticEvents.ADD_TO_WISHLIST,
              product: { ...productItem, name: name, hitIndex: index || 0, selectedQuantity: 1 },
              selectedVariantId: selectedVariant?.variantId,
              source,
              sourceId,
            },
          });
          if (indexName) {
            algoliaWishlistConversionClient(
              indexName,
              eventParam.WISHLIST_ADD,
              variantId,
              queryID,
              user?.magrabi_id,
            );
          }
        }
      }
    } catch (error) {
      setIsWishlisted(false);
      removeProductFromWishlist(variantId);
      showToast({
        text: translations?.common?.common_add_to_cart_error_toast_message ?? "",
        lang: language,
        icon: HeartIcon,
      });
    }
  };
  const handleAddToCart = async () => {
    setIsLoadingAddToCart(true);
    const response = await addToCartById(locale as string, selectedVariant?.variantId || "", {
      magrabiId: user?.magrabi_id,
      indexName,
      objectID,
      queryID,
    });
    if (response?.checkout?.lines) {
      if (onDeferToast) {
        onDeferToast(selectedProductImage ?? "");
      } else {
        showToast({
          imgUrl: selectedProductImage,
          text: translations?.common?.common_added_to_cart_toast_message ?? "",
          lang: language,
          redirectUrl: "/cart",
        });
      }

      const count = getCartCount(response?.checkout?.lines ?? []) ?? cartCount;
      updateCartCount(count);
      addCartVariantId(variantId);
      setIsLoadingAddToCart(false);

      await handlePostAnalytics({
        eventType: analyticEventTypes.ACTIONS,
        data: {
          target: analyticEvents.ADD_TO_CART,
          product: { ...productItem, name: name, hitIndex: index || 0, selectedQuantity: 1 },
          selectedVariantId: variantId,
          source,
          sourceId,
          buyFlow: analyticBuyFlows.QUICK_BUY,
        },
      });
    } else if (!response?.checkout && response && hasArrayElements(response?.errors)) {
      const [error] = response?.errors || [];
      let errorMessage = "";
      if (error?.code === cartErrors.INSUFFICIENT_STOCK_ERROR_CODE) {
        errorMessage =
          translations?.common?.common_add_to_cart_insufficient_stock_toast_message ?? "";
      } else if (error?.code === cartErrors.QUANTITY_GREATER_THAN_LIMIT_ERROR_CODE) {
        errorMessage =
          translations?.common?.common_add_to_cart_quantity_exceeds_checkout_limit_toast_message ??
          "";
      } else if (error?.code === cartErrors.PRODUCT_UNAVAILABLE_FOR_PURCHASE_ERROR_CODE) {
        errorMessage =
          translations?.common?.common_product_unavailable_for_purchase_toast_message ?? "";
      } else {
        errorMessage = translations?.common?.common_add_to_cart_error_toast_message ?? "";
      }
      showToast({
        imgUrl: selectedProductImage,
        text: errorMessage,
        lang: language,
        redirectUrl: "/cart",
      });
      setIsLoadingAddToCart(false);
    }
    setIsLoadingAddToCart(false);
  };

  const handleProductClickEvent = () => {
    // Fire-and-forget analytics - don't block navigation
    // Analytics should not prevent user from navigating to PDP
    if (queryID && indexName) {
      algoliaClickEventClient(
        algoliaEventParam.PRODUCT_CLICK,
        indexName || "",
        eventParam.PRODUCT_CLICK,
        objectID || selectedVariant.variantId,
        queryID || "",
        index || 0,
        user?.magrabi_id,
      ).catch((e) => {
        console.log("Error while Product Click Event : ", e);
      });
    } else if (indexName) {
      algoliaClickedObjectIDSClient(
        algoliaEventParam.CLICKED_OBJECTIDS,
        indexName,
        eventParam.PRODUCT_CLICK,
        objectID || selectedVariant.variantId,
        user?.magrabi_id,
      ).catch((e) => {
        console.log("Error while Product Click Event : ", e);
      });
    }

    // Fire-and-forget GA analytics
    handlePostAnalytics({
      eventType: analyticEventTypes.ACTIONS,
      data: {
        target: analyticEvents.PRODUCT_CLICK,
        product: { ...productItem, name: name, hitIndex: index || 0, selectedQuantity: 1 },
        selectedVariantId: selectedVariant?.variantId,
        source,
        sourceId,
      },
    }).catch((e) => {
      console.log("Error while posting analytics : ", e);
    });
  };

  // console.log("Source Data", { source, sourceId, index });

  let constructedLink = productLinkConstructor(
    selectedVariant?.landingPageBaseUrl,
    slug,
    selectedVariant?.variantId,
    source,
    index?.toString(),
    sourceId,
    undefined,
    queryID,
  )?.href;

  if (typeof constructedLink === "object") {
    const { pathname, query } = constructedLink || {};
    const queryString = query ? new URLSearchParams(query)?.toString() : "";
    constructedLink = `${pathname}${queryString ? `?${queryString}` : ""}`;
  }

  const isMounted = useRef<Boolean>(false);

  //DM-1927 - skip as of now - as further issues
  //with unmount still exist
  //useEffect(() => {
  //  isMounted.current = true;
  //  window.addEventListener("scroll", handleScroll);

  //  return () => {
  //    isMounted.current = false;
  //    window.removeEventListener("scroll", handleScroll);
  //  };
  //}, []);

  const handleScroll = () => {
    if (!cardRef || !cardRef.current || !isMounted.current) return;

    const lowerBound = window.innerHeight;
    const cardTop = cardRef.current.getBoundingClientRect().top;

    if (cardTop >= lowerBound) {
      setResetProductImageCarousel(true);
      setOpenVariantCarousel(false);
    }

    const scrollTop = document.documentElement.scrollTop;
    const imageCarouselDiff = scrollTop - prevScrollPosImageCarousel.current;
    const variantCarouselDiff = scrollTop - prevScrollPosVariantCarousel.current;

    if (imageCarouselDiff < 0 || variantCarouselDiff < 0) {
      prevScrollPosImageCarousel.current = scrollTop;
      prevScrollPosVariantCarousel.current = scrollTop;
      return;
    }

    if (imageCarouselDiff >= 200) {
      setResetProductImageCarousel(true);
      prevScrollPosImageCarousel.current = scrollTop;
    }

    if (variantCarouselDiff >= 40) {
      setOpenVariantCarousel(false);
      prevScrollPosVariantCarousel.current = scrollTop;
    }
  };

  //hooks
  useEffect(() => {
    setIsWishlisted(wishlistedProducts?.includes(variantId));
  }, [selectedVariant?.variantId, wishlistedProducts]);

  // console.log("updating state rerendering");

  return (
    <div className={cn("flex flex-col bg-white md:max-w-full")} ref={cardRef}>
      <div className={cn("relative aspect-[0.83/1]")}>
        <CLink href={constructedLink ?? "/"} onClick={handleProductClickEvent}>
          <ProductImage
            productImages={productImages}
            productImageStyle={{
              ...productImageStyles,
            }}
            showProductCarousel={showProductCarousel}
            altText={altText}
            index={index}
          />
        </CLink>

        {showProductCarousel ? (
          productImages.length > 0 ? (
            <CLink href={constructedLink ?? "/"} onClick={handleProductClickEvent}>
              <ProductImageCarousel
                productImages={productImages}
                productImageStyle={{ ...productImageStyles }}
                altText={altText}
                isNudgeAnimationPresent={isNudgeAnimationPresent}
                resetImageCarousel={resetProductImageCarousel}
                handleSetResetProductImageCarousel={handleSetResetProductImageCarousel}
              />
            </CLink>
          ) : (
            <Image src={NoImageSrc} alt={"no-image"} fill className="md:hidden" />
          )
        ) : (
          <></>
        )}
        <div className="absolute top-0 flex w-full items-center justify-between px-1 py-2 md:px-2">
          <div>{productTileBadge}</div>
          {likeBtn && (
            <AddToWishList
              isWishlisted={isWishlisted}
              onAddToWishList={isWishlisted ? handleRemoveFromWishlist : handleAddToWishlist}
            />
          )}
        </div>
        <div className="absolute bottom-[2px] flex w-full items-center justify-between p-2">
          {tryOn ? (
            <div
              role="button"
              className="flex h-[21px] cursor-pointer gap-1 bg-white px-2 py-1 shadow-[0_1.5px_4px_rgba(234,234,234,0.5)]"
              onClick={() => setVtoOpen(true)}
            >
              <span className="text-size-10 font-medium">
                {translations?.common?.common_try_them_on_label ?? "Try them on"}
              </span>
              <Image
                src={SpecsIcon.src}
                alt={getFieldValue(language, "Virtual try on", "محاولة افتراضية") ?? ""}
                height={10}
                width={24}
              />
            </div>
          ) : (
            <div></div>
          )}
          {addToCart && !isNonTransactional && (
            isAlreadyInCart ? (
              <div
                title={translations?.common?.common_added_to_cart_toast_message ?? "Added to Cart"}
                className="flex h-5 w-5 items-center justify-center bg-white shadow-[0_0.5px_4px_rgba(234,234,234,0.5)] md:h-7 md:w-7"
              >
                <CheckIcon className="h-[12px] w-[14px] md:h-[13px] md:w-[14px]" />
              </div>
            ) : (
              <button
                title={translations?.plp?.plp_add_to_cart_label ?? "Add to Cart"}
                onClick={handleAddToCart}
                disabled={isLoadingAddToCart}
                className="flex h-5 w-5 cursor-pointer items-center justify-center bg-white shadow-[0_0.5px_4px_rgba(234,234,234,0.5)] md:h-7 md:w-7"
              >
                {isLoadingAddToCart ? (
                  <Spinner />
                ) : (
                  <PlusIcon
                    className="h-[13px] w-[13px] md:h-[15px] md:w-[15px]"
                    //@ts-ignore
                    title={translations?.plp?.plp_add_to_cart_label ?? "Add to Cart"}
                  />
                )}
              </button>
            )
          )}
        </div>
      </div>

      <div className="flex w-full items-center justify-center">
        <VisibilitySensor onVisible={onVisible}>
          <div />
        </VisibilitySensor>
      </div>

      <div
        className={cn(
          "flex flex-col gap-2 bg-white px-1",
          showProductCarousel
            ? isChevronVariant && productVariants && productVariants?.length > 1
              ? "h-60"
              : "h-[153px]"
            : "",
        )}
      >
        <div className="flex flex-col gap-[6px] pl-1 pt-[14px] md:py-5">
          {name && (
            <h2
              className={cn(
                "font-semibold",
                isChevronVariant
                  ? "text-base leading-[21px]"
                  : "text-sm leading-[18px] md:text-base",
              )}
            >
              {name}
            </h2>
          )}
          {description && (
            <h3
              className={cn(
                "max-w-[180px] truncate font-normal",
                isChevronVariant ? "text-sm leading-[18px]" : "text-xs md:text-sm",
              )}
              title={description}
            >
              {description}
            </h3>
          )}
          {pricing && (
            <ProductPrice
              locale={locale as string}
              inColumnView={isChevronVariant}
              priceInfo={plpPriceInfo}
            />
          )}
          {/*MEPS-980 Show promotions for all products, even those with a single variant */}
          {!isChevronVariant && productVariants && (
            <>
              {productVariants.length > 1 && (
                <div className="flex items-center gap-[3px]">
                  {productVariants?.slice(0, 3)?.map((item: VariantItem, idx) => (
                    <div
                      key={idx}
                      className={`h-2 w-2 rounded-full`}
                      style={{ backgroundColor: item.hexcode }}
                    ></div>
                  ))}
                  {productVariants?.length > 3 && (
                    <div className="text-xs font-normal">+{productVariants.length - 3}</div>
                  )}
                </div>
              )}
              {promotionalDetails && promotionalDetails.length > 0 && (
                <div className={`flex flex-col gap-1 md:gap-[6px]`}>
                  {promotionalDetails?.map((data, index) => {
                    const promoName = getPromotionBadgeName(data?.promotion, language);
                    if (!promoName) return null;
                    return (
                      <p key={index} className="text-xs font-normal text-secondary-1 md:text-sm">
                        <RenderCurrencyInText text={promoName} />
                      </p>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {isChevronVariant && productVariants && productVariants.length > 1 && (
            <ProductVariantsCarousel
              productVariants={productVariants}
              promotionalDetails={promotionalDetails || []}
              handleSelectVariant={handleSelectVariant}
              selectedVariant={selectedVariant}
              translations={translations}
              openVariantCarousel={openVariantCarousel}
              handleOpenVariantCarousel={handleSetOpenVariantCarousel}
            />
          )}
          {/* MEPS-980Show promotions for all products, even those with a single variant, but avoid showing variant collapse UI when there's only one variant */}
          {isChevronVariant &&
            productVariants &&
            promotionalDetails &&
            promotionalDetails.length > 0 && (
              <div
                className={cn(
                  `flex gap-1 md:gap-[6px]`,
                  openVariantCarousel ? "mt-[2px] flex-row gap-5" : "flex-col",
                )}
              >
                {promotionalDetails?.map((data, index) => {
                  const promoName = getPromotionBadgeName(data?.promotion, language);
                  if (!promoName) return null;
                  return (
                    <p
                      key={index}
                      className="text-[13px] font-normal leading-[17px] text-secondary-1"
                    >
                      <RenderCurrencyInText text={promoName} />
                    </p>
                  );
                })}
              </div>
            )}
        </div>
      </div>
      <VtoModal
        isOpen={vtoOpen}
        frameId={{
          ean: ean ?? "",
          sku: sku ?? "",
        }}
        toggle={() => setVtoOpen(false)}
      />
    </div>
  );
};

export default memo(ProductCard);
